import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface IntakeData {
  // Identity (Step 1)
  fullName: string;
  licenseNumber: string;
  // Contact (Step 2)
  email: string;
  phone: string;
  // Brand (Step 3)
  brokerageName: string;
  // Object URLs (blob:) for in-memory preview — not persisted to sessionStorage
  headshotDataUrl: string | null;
  headshotFileName: string | null;
  brokerageLogoDataUrl: string | null;
  brokerageLogoFileName: string | null;
  // Actual File objects — not persisted (can't serialize File to JSON)
  headshot: File | null;
  brokerageLogo: File | null;
  // Market (Step 5)
  primaryMarket: string;
  specialty: string;
  // Brand colors (Step 6)
  brandColors: string[];
  brandColorsMode: 'pick' | 'choose_for_me';
  // Typography (Step 7)
  typography: string | 'choose_for_me';
  // Content (Steps 8-10)
  bio: string;
  tagline: string;
  socialHandles: { instagram?: string; facebook?: string; linkedin?: string };
  // Optional (Steps 11-12)
  referenceSites: string[];
  desiredDomain: string;
  // Integrations (Step 13)
  idxPreferences: string;
  // Testimonials (Step 14)
  testimonials: string[];
  // Notes (Step 15)
  additionalNotes: string;
}

export interface IntakeState {
  track: 'staff' | 'self' | null;
  step: number;
  data: IntakeData;

  setTrack: (track: 'staff' | 'self') => void;
  setStep: (step: number) => void;
  updateData: (partial: Partial<IntakeData>) => void;
  setFile: (field: 'headshot' | 'brokerageLogo', file: File | null) => void;
  reset: () => void;
  prefillFromProfile: (profile: Partial<IntakeData>) => void;
}

const DEFAULT_DATA: IntakeData = {
  fullName: '',
  licenseNumber: '',
  email: '',
  phone: '',
  brokerageName: '',
  headshotDataUrl: null,
  headshotFileName: null,
  brokerageLogoDataUrl: null,
  brokerageLogoFileName: null,
  headshot: null,
  brokerageLogo: null,
  primaryMarket: '',
  specialty: '',
  brandColors: ['#4E1F60'],
  brandColorsMode: 'pick',
  typography: 'choose_for_me',
  bio: '',
  tagline: '',
  socialHandles: {},
  referenceSites: ['', '', ''],
  desiredDomain: '',
  idxPreferences: '',
  testimonials: [],
  additionalNotes: '',
};

export const useIntakeStore = create<IntakeState>()(
  persist(
    (set) => ({
      track: null,
      step: 1,
      data: { ...DEFAULT_DATA },

      setTrack: (track) => set({ track }),

      setStep: (step) => set({ step }),

      updateData: (partial) =>
        set((state) => ({
          data: { ...state.data, ...partial },
        })),

      setFile: (field, file) => {
        if (!file) {
          if (field === 'headshot') {
            set((state) => {
              if (state.data.headshotDataUrl) URL.revokeObjectURL(state.data.headshotDataUrl);
              return {
                data: {
                  ...state.data,
                  headshot: null,
                  headshotDataUrl: null,
                  headshotFileName: null,
                },
              };
            });
          } else {
            set((state) => {
              if (state.data.brokerageLogoDataUrl) URL.revokeObjectURL(state.data.brokerageLogoDataUrl);
              return {
                data: {
                  ...state.data,
                  brokerageLogo: null,
                  brokerageLogoDataUrl: null,
                  brokerageLogoFileName: null,
                },
              };
            });
          }
          return;
        }

        // Use object URL — stays in memory, never hits sessionStorage
        const objectUrl = URL.createObjectURL(file);
        if (field === 'headshot') {
          set((state) => {
            if (state.data.headshotDataUrl) URL.revokeObjectURL(state.data.headshotDataUrl);
            return {
              data: {
                ...state.data,
                headshot: file,
                headshotDataUrl: objectUrl,
                headshotFileName: file.name,
              },
            };
          });
        } else {
          set((state) => {
            if (state.data.brokerageLogoDataUrl) URL.revokeObjectURL(state.data.brokerageLogoDataUrl);
            return {
              data: {
                ...state.data,
                brokerageLogo: file,
                brokerageLogoDataUrl: objectUrl,
                brokerageLogoFileName: file.name,
              },
            };
          });
        }
      },

      reset: () =>
        set({
          track: null,
          step: 1,
          data: { ...DEFAULT_DATA },
        }),

      prefillFromProfile: (profile) =>
        set((state) => ({
          data: { ...state.data, ...profile },
        })),
    }),
    {
      name: 'bls-intake-store',
      storage: createJSONStorage(() => sessionStorage),
      // Exclude File objects and object URLs — only persist filenames
      partialize: (state) => ({
        track: state.track,
        step: state.step,
        data: {
          ...state.data,
          headshot: null,
          brokerageLogo: null,
          headshotDataUrl: null,
          brokerageLogoDataUrl: null,
        },
      }),
    },
  ),
);
